<!-- Standard image picker is afflicted with a bug on iPhone (not affecting iPad nor Mac)
where it will cause a sudden page reload when selecting an image. This component is a bit of an ugly AI produced workaround
-->
<template>
  <div class="image-picker mb-5 image-picker mb-5">
    <div
      class="input-slot"
      data-cy="image-picker-input"
      :label="label"
      :hint="hint"
      :persistent-hint="true"
    >
      <v-col class="container">
        <v-img
          v-if="imageId"
          :src="imageUrl"
          :lazy-src="showLazy ? 'https://picsum.photos/128/?blur=5' : undefined"
          contain
          min-height="200"
          max-height="250"
        />
        <v-row class="mt-5 pb-2" justify="space-around">
          <v-btn
            id="imagePickerRandomButton"
            :aria-label="t('aria.label.randomizeImage')"
            type="button"
            :ripple="false"
            data-cy="image-picker-btn-random"
            class="button"
            :disabled="!images || images.length === 0"
            @click.stop.prevent="random"
            @touchend.stop.prevent="random"
          >
            {{ t('components.imagePicker.random') }}
          </v-btn>
          <v-dialog
            v-model="dialog"
            persistent
            scrollable
            max-width="800px"
            eager
            :retain-focus="false"
            :close-on-content-click="false"
            :z-index="3000"
            content-class="image-picker-dialog-content"
            data-cy="image-picker-dialog"
            fullscreen
            @update:model-value="onDialogInput"
          >
            <template #activator>
              <v-btn
                id="imagePickerChoiceButton"
                :aria-label="t('aria.label.selectImage')"
                :ripple="false"
                type="button"
                data-cy="image-picker-btn-pick"
                class="button"
                color="primary"
                :disabled="!images || images.length === 0"
                @click.stop="openDialog"
                @touchend.stop="openDialog"
              >
                {{ t('components.imagePicker.pick') }}
              </v-btn>
            </template>
            <image-picker-view
              v-model="imageId"
              data-cy="image-picker-view-close-btn"
              :images="images"
              :categories="categories"
              @close="requestClose"
            />
          </v-dialog>
        </v-row>
        <v-progress-linear indeterminate :active="loading" />
      </v-col>
    </div>
  </div>
</template>

<script lang="ts">
import ImagePickerView from '@/views/image/ImagePickerIOSView.vue';
import { defineComponent, ref, computed, watch, onBeforeUnmount } from 'vue';
import { DonationImage } from '@/interfaces/donation/donationImage';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  components: { ImagePickerView },
  props: {
    modelValue: { type: Number, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    images: { type: Array as () => DonationImage[], default: () => [] },
    categories: { type: Array, default: () => [] },
    showLazy: { type: Boolean, default: true },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t } = useLabel();

    const dialog = ref(false);
    const canClose = ref(false);
    const loading = ref(false);
    const imageId = ref(props.modelValue as number | null);
    const scrollYLock = ref(0);

    const imageUrl = computed(() => {
      const list = Array.isArray(props.images) ? (props.images as DonationImage[]) : [];
      const image = list.find((i) => i.id === imageId.value);
      return image ? image.url : undefined;
    });

    const openDialog = () => {
      canClose.value = false;
      dialog.value = true;
    };

    const requestClose = () => {
      canClose.value = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.value = false;
          setTimeout(() => {
            canClose.value = false;
          }, 50);
        });
      });
    };

    const onDialogInput = (nextOpen: boolean) => {
      if (nextOpen) {
        dialog.value = true;
        return;
      }
      dialog.value = !canClose.value;
    };

    const lockBodyScroll = () => {
      scrollYLock.value = window.scrollY || window.pageYOffset || 0;
      const b = document.body.style;
      b.position = 'fixed';
      b.top = `-${scrollYLock.value}px`;
      b.left = '0';
      b.right = '0';
      b.width = '100%';
    };

    const unlockBodyScroll = () => {
      const y = scrollYLock.value || 0;
      const b = document.body.style;
      b.position = '';
      b.top = '';
      b.left = '';
      b.right = '';
      b.width = '';
      window.scrollTo(0, y);
    };

    const random = () => {
      loading.value = true;
      const list = Array.isArray(props.images) ? props.images : [];
      if (!list.length) {
        loading.value = false;
        return;
      }
      let newId = list[Math.floor(Math.random() * list.length)].id;
      if (list.length > 1) {
        while (newId === imageId.value) newId = list[Math.floor(Math.random() * list.length)].id;
      }
      imageId.value = newId;
      loading.value = false;
    };

    watch(
      imageId,
      (val) => {
        if (val) emit('update:modelValue', val);
      },
      { immediate: true }
    );
    watch(
      () => props.modelValue,
      (val: number | null) => {
        imageId.value = val;
      }
    );
    watch(dialog, (open) => {
      if (open) lockBodyScroll();
      else unlockBodyScroll();
    });

    onBeforeUnmount(() => {
      unlockBodyScroll();
    });

    return {
      dialog,
      canClose,
      loading,
      imageId,
      scrollYLock,
      imageUrl,
      openDialog,
      requestClose,
      onDialogInput,
      lockBodyScroll,
      unlockBodyScroll,
      random,
      t,
    };
  },
});
</script>

<style>
.image-picker .v-input__slot,
.image-picker .input-slot {
  flex-direction: column;
  align-items: start;
}
.image-picker-dialog-content {
  overflow: hidden !important;
}
</style>
<style scoped>
.container {
  width: 100%;
  flex-shrink: 0;
  padding: 0 !important;
}
.image {
  background-color: grey;
  height: 200px;
  width: 100%;
}
</style>
